import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
} from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useGetBasicFilters from "src/aadeCase/hooks/useGetBasicFilters";
import useGetChamberUsers from "../data-access/useGetChamberUsers";
import { Box, Typography } from "@mui/material";
import CaseFilters from "../components/CaseFilters";
import { useAuth } from "src/shared/context/useAuth";
import CaseTab from "../shared/CaseTab";
import { TCaseFilter } from "../data-access/types";
import useGetPastAadeCases from "../data-access/useGetPastAadeCases";
import i18n from "src/shared/services/languages/i18n";
import getTableLang from "src/shared/services/languages/MaterialReactTable/getTableLang";
import useCustomMaterialTable from "src/shared/hooks/useCustomMaterialTable";
import { format, parseISO } from "date-fns";
import { Link } from "react-router-dom";
import AadeCaseSearchOrchestrator from "src/aadeCase/components/AadeCaseSearchOrchestrator";

export default function PastAadeCases() {
  const { t } = useTranslation();

  const { isOrgAdmin } = useAuth();

  const [tab, setTab] = useState<Number>(0);
  const [searchFilters, setSearchFilters] = useState<FieldValues>({});
  const [enableSearch, setEnableSearch] = useState<Boolean>(true);

  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageSize: 10,
    pageIndex: 0,
  });

  const basicFilters = useGetBasicFilters();
  const { data: chamberUserResponse, isLoading: isLoadingChamberUsers } =
    useGetChamberUsers(isOrgAdmin);

  useEffect(() => {
    setSearchFilters({});
    setEnableSearch(true);
    setPaginationState({
      pageIndex: 0,
      pageSize: 10,
    });
  }, [tab]);

  const pastAadeCaseFilters: TCaseFilter[] = [
    ...basicFilters,
    {
      label: t("Διεκπεραίωση υπόθεσης από"),
      component: { type: "datePicker" },
      name: "completed_date_from",
    },
    {
      label: t("Διεκπεραίωση υπόθεσης έως"),
      component: { type: "datePicker" },
      name: "completed_date_to",
    },
    ...(isOrgAdmin && tab === 1
      ? [
          {
            label: t("Αρμόδιος Χρήστης"),
            component: {
              type: "autoComplete" as const,
              options: chamberUserResponse?.data.chamberUsers,
              loading: isLoadingChamberUsers,
            },
            name: "chamberUser",
          },
        ]
      : []),
  ];

  const title = t(
    "Διεκπεραιωμένες Υποθέσεις Εκκαθάρισης που διαχειρίζεστε προσωπικά ή άλλοι υπάλληλοι της Υ. Γ.Ε.ΜΗ.",
  );

  const subtitle = t(
    "Στην οθόνη αυτή εμφανίζονται υποθέσεις που έχουν διεκπεραιωθεί από εσάς ή από άλλον υπάλληλο της Υ. Γ.Ε.ΜΗ.",
  );

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: t("Αριθμός υπόθεσης"),
        accessorKey: "case_id",
        accessorFn: (originalRow) => {
          return (
            <Link
              to={`${"/chamber/manageAadeCaseApplication"}/${originalRow.case_id}`}
            >
              {originalRow.case_id}
            </Link>
          );
        },
      },
      {
        header: t("Αριθμός Γ.Ε.ΜΗ."),
        accessorKey: "company_id",
      },
      {
        header: t("ΑΦΜ"),
        accessorKey: "afm",
      },
      {
        header: t("Νομική Μορφή"),
        accessorKey: "legalType",
      },
      {
        header: t("Πλήθος αναντιστοιχιών"),
        accessorKey: "subcase_count",
      },
      ...(tab === 0
        ? []
        : [
            {
              header: t("Αρμόδιος Χρήστης"),
              accessorKey: "responsible_user",
            },
          ]),
      {
        header: t("Ημερομηνία Διεκπεραίωσης"),
        accessorKey: "date_created",
        accessorFn: (originalRow) => {
          return (
            <>
              {format(
                parseISO(originalRow.completed_date),
                "dd/MM/yyyy HH:mm:ss",
              )}
            </>
          );
        },
      },
      {
        header: t("Ημερομηνία Δημιουργίας"),
        accessorKey: "date_created",
        accessorFn: (originalRow) => {
          return (
            <>
              {format(
                parseISO(originalRow.date_created),
                "dd/MM/yyyy HH:mm:ss",
              )}
            </>
          );
        },
      },
    ],
    [i18n.language, tab],
  );

  const { data: pastAadeCasesResponse, isLoading: isLoadingPastAadeCases } =
    useGetPastAadeCases({
      filters: searchFilters,
      enabled: enableSearch,
      setEnableSearch: setEnableSearch,
      tab: tab,
      paginationState: paginationState,
    });

  const table = useCustomMaterialTable({
    columns,
    data: pastAadeCasesResponse?.data.rows ?? [],
    onPaginationChange: (updater) => {
      setPaginationState((previous) => updater(previous));
      setEnableSearch(true);
    },
    localization: getTableLang(),
    rowCount: pastAadeCasesResponse?.data.rowCount ?? 0,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableSorting: false,
    state: {
      isLoading: isLoadingPastAadeCases,
      showProgressBars: isLoadingPastAadeCases,
      pagination: paginationState,
    },
    manualPagination: true,
  });
  const onSuccess = (data: FieldValues) => {
    setSearchFilters({
      ...data,
      date_from: data.date_from ? format(data.date_from, "yyyy-MM-dd") : null,
      date_to: data.date_to ? format(data.date_to, "yyyy-MM-dd") : null,
      legalType: data.legalType ? data.legalType.value : null,
      chamberUser: data.chamberUser ? data.chamberUser.value : null,
      completed_date_from: data.completed_date_from
        ? format(data.completed_date_from, "yyyy-MM-dd")
        : null,
      completed_date_to: data.completed_date_to
        ? format(data.completed_date_to, "yyyy-MM-dd")
        : null,
    });
    setEnableSearch(true);
  };

  return (
    <AadeCaseSearchOrchestrator
      title={title}
      subtitle={subtitle}
      filters={pastAadeCaseFilters}
      tabComponent={
        isOrgAdmin ? <CaseTab value={tab} setValue={setTab} /> : null
      }
      handleSearch={onSuccess}
      table={table}
    />
  );
}
